import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { GatewayAssignments } from "./paymentGateway";
import { bandFor, TIER_BANDS, type TierBand } from "../domain/tiers";

/**
 * What a new operator still has to do, shown on its dashboard (owner
 * 2026-10-31), and the type and upstream its Owner may change after sign-up.
 * radaccess /access/operator/{ns}/setup-status and /setup/*.
 */
export interface SetupStatus {
  tenant: boolean;
  selfonboarded?: boolean;
  pan?: string;
  gstin?: string;
  cin?: string;
  cinverified?: boolean;
  state?: string;
  trialendsat?: string;
  /** running | restricted | locked | closing; absent for live and test operators. */
  trialstage?: string;
  /** True only when the server refuses by trial stage (RADACCESS_TRIAL_GATE). */
  trialgate?: boolean;
  operatortype: string;
  upstream?: string;
  holdslicense: boolean;
  licenseverified: boolean;
  taxonfile: boolean;
  taxverified: boolean;
  officeonfile: boolean;
  invoiceemail?: string;
  invoiceemailverified: boolean;
  smsbalancepaise: number;
  smspendingpaise: number;
  invoicesissued: boolean;
  /** null: the portal did not answer. Unknown is not "not ready". */
  portalready: boolean | null;
  /** The billing count today. null: not read. */
  activesubscribers?: number | null;
}

const path = (ns: string, leaf: string) => `/operator/${encodeURIComponent(ns)}/${leaf}`;

export async function getSetupStatus(i: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SetupStatus> {
  return (await callGateway<SetupStatus>(i, config().gatewayBase, path(ns, "setup-status"), signal ? { signal } : {})).data;
}

export const setSetupType = (i: CallerIdentity, ns: string, operatortype: string, upstreamnamespace: string) =>
  callGateway<{ operatortype: string }>(i, config().gatewayBase, path(ns, "setup/type"), {
    method: "PUT",
    body: { operatortype, upstreamnamespace },
  });

export const saveLoginEmailForInvoices = (i: CallerIdentity, ns: string) =>
  callGateway<{ invoiceemail: string }>(i, config().gatewayBase, path(ns, "setup/invoice-email"), { method: "PUT" });

/** A passed check is the only way a self-onboarded operator's PAN, GSTIN or CIN changes. */
export const reverify = (i: CallerIdentity, ns: string, kind: "pan" | "gstin" | "cin", value: string) =>
  callGateway<{ status: string }>(i, config().gatewayBase, path(ns, "setup/verify"), {
    method: "POST",
    body: { kind, value },
  });

export interface SetupNotice {
  key: string;
  severity: "warning" | "info";
  text: string;
  to?: string;
  action?: string;
}

/** Facts read elsewhere; undefined means not known, and no notice is raised on it. */
export interface SetupFacts {
  gateway?: GatewayAssignments | undefined;
  activePlans?: number | undefined;
  loginEmailVerified?: boolean | undefined;
}

export const SMS_PACK = "₹350 including GST";

export function setupNotices(s: SetupStatus, f: SetupFacts): SetupNotice[] {
  if (!s.tenant) return [];
  const out: SetupNotice[] = [];
  if (s.smsbalancepaise + s.smspendingpaise <= 0) {
    out.push({ key: "sms", severity: "warning", to: "/sms/fund", action: "Add funds",
      text: `Add funds to your SMS fund: ${SMS_PACK} buys 1000 messages, or a multiple. Your first top-up can be ₹100 including GST. We email you the invoice.` });
  }
  const effective = f.gateway?.effective;
  if (f.gateway && !effective) {
    const why = f.gateway.effectiveerror;
    out.push({ key: "gateway", severity: "warning", to: "/payments/gateway", action: "Set up",
      text: `Register a payment gateway: until then you cannot accept online payments.${why ? ` ${why}` : ""}` });
  } else if (effective?.account.connectorname === "upi_qr") {
    out.push({ key: "gateway", severity: "warning", to: "/payments/gateway", action: "Set up",
      text: "You accept manual UPI only. Register a payment gateway to accept online payments." });
  } else if (effective?.account.mode === "test") {
    out.push({ key: "gateway", severity: "warning", to: "/payments/gateway", action: "Set up",
      text: "Your payment gateway is in test mode, so it does not take real money." });
  }
  if (!s.invoiceemail || !s.invoiceemailverified) {
    out.push(f.loginEmailVerified === false
      ? { key: "email", severity: "warning", to: "/settings/security", action: "Verify email",
          text: "Verify your email: we send your invoices to it." }
      : { key: "email", severity: "warning", action: "use-login-email",
          text: "Choose where we send your invoices." });
  }
  if (f.activePlans === 0) {
    out.push({ key: "plans", severity: "warning", to: "/plans/create", action: "Create a plan",
      text: "Create your first plan: you have nothing on sale yet." });
  }
  if (s.portalready === false) {
    out.push({ key: "portal", severity: "info", text: "Your customer portal is not ready yet. Ankh's team is setting it up." });
  }
  if (["ISP", "VNO"].includes(s.operatortype) && !s.licenseverified) {
    out.push({ key: "licence", severity: "warning",
      text: "Send your licence to Ankh's team. They check it offline, and licensed sales wait for that." });
  }
  if (s.operatortype === "OPERATOR" && !s.upstream) {
    out.push({ key: "upstream", severity: "warning", to: "/operator/profile", action: "Add upstream",
      text: "Name your upstream provider: internet invoices go in its name." });
  }
  if (!s.taxverified) {
    out.push({ key: "tax", severity: "warning", to: "/operator/profile", action: "Open profile",
      text: "Your tax details are not verified yet." });
  }
  if (s.state === "TRIAL" && s.trialendsat) {
    const ends = new Date(s.trialendsat).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" });
    out.push({ key: "trial", severity: "info", text: `You are on AnkhOS Launch. Your free trial ends on ${ends}; billing then follows your active subscribers.` });
  }
  if (!s.officeonfile) {
    out.push({ key: "office", severity: "warning", to: "/operator/profile", action: "Open profile",
      text: "Your registered office is missing." });
  }
  return out;
}

export interface PlanOutlook {
  band: TierBand;
  next?: TierBand | undefined;
  count: number;
  /** Share of the allowance in use, 0 to 100. Undefined on the unbounded band. */
  used?: number | undefined;
  /** 80% of the allowance or more: time to tell the operator what comes next. */
  nearLimit: boolean;
}

/** The band follows the count; nothing here chooses one. */
export function planOutlook(count: number): PlanOutlook {
  const band = bandFor(count);
  const next = TIER_BANDS[TIER_BANDS.indexOf(band) + 1];
  const used = band.to === null ? undefined : Math.min(100, Math.round((count / band.to) * 100));
  return { band, next, count, used, nearLimit: band.to !== null && count * 100 >= band.to * 80 && next !== undefined };
}

/** The banner an operator on a trial sees; null when there is none. */
export interface TrialBanner {
  severity: "info" | "warning" | "error";
  text: string;
}

const DAY = 24 * 60 * 60 * 1000;
const istDate = (ms: number) =>
  new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric" }).format(new Date(ms));

/**
 * Owner 2026-11-02: reminders before the trial ends; from the day after, no new
 * customers or plans; from 15 days after, no new recharges either. Subscribers'
 * internet runs on. While the server's gate is off, the banner says what WILL
 * pause, never that something already has.
 */
export function trialBanner(s: SetupStatus, now: number): TrialBanner | null {
  if (!s.tenant || !s.trialstage || !s.trialendsat) return null;
  const end = Date.parse(s.trialendsat);
  if (Number.isNaN(end)) return null;
  const ends = istDate(end);
  const recharges = istDate(end + 15 * DAY);
  const goLive = "Contact AnkhOS support to go live.";
  switch (s.trialstage) {
    case "running":
      if (end - now > 15 * DAY) return null;
      return { severity: "info", text: `Your free trial ends on ${ends}. ${goLive}` };
    case "restricted":
      return {
        severity: "warning",
        text: s.trialgate
          ? `Your free trial ended on ${ends}. New customers and plans are paused until you go live, and new recharges pause from ${recharges}. ${goLive}`
          : `Your free trial ended on ${ends}. New customers and plans will be paused until you go live, and new recharges from ${recharges}. ${goLive}`,
      };
    case "locked":
    case "closing":
      return {
        severity: "error",
        text: s.trialgate
          ? `Your free trial ended on ${ends}. New customers, plans and recharges are paused until you go live. Your customers' internet keeps working until their plans end. ${goLive}`
          : `Your free trial ended on ${ends}. New customers, plans and recharges will be paused until you go live. Your customers' internet keeps working until their plans end. ${goLive}`,
      };
  }
  return null;
}
